import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { api, errorMessage } from "./api/client";
import type { Me, Meta } from "./api/types";
import { BackChevron } from "./components/Icons";
import { ToastHost } from "./components/Bits";
import { Ctx, emptyDraft, emptyFavDraft, needsOnboarding, useApp, welcomed, type AppCtx, type BoardDraft, type FavDraft } from "./state";
import { SignIn } from "./views/SignIn";
import { Onboarding } from "./views/Onboarding";
import { FavoriteStep } from "./views/FavoriteStep";
import { Board } from "./views/Board";
import { Report } from "./views/Report";
import { History } from "./views/History";
import { Profile } from "./views/Profile";

/* ---------- Theme (same behaviour and storage key as the demo) ---------- */
function useTheme() {
  const mq = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;
  const read = useCallback(() => {
    const t = document.documentElement.dataset.theme;
    return t ? t === "dark" : !!mq?.matches;
  }, [mq]);
  const [dark, setDark] = useState(read);
  useEffect(() => {
    if (!mq) return;
    const on = () => setDark(read());
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, [mq, read]);
  const toggle = () => {
    const next = read() ? "light" : "dark";
    document.documentElement.dataset.theme = next;
    try {
      localStorage.setItem("drape.theme", next);
    } catch {
      /* ignore */
    }
    setDark(next === "dark");
  };
  return { dark, toggle };
}

function Nav({ me }: { me: Me | null }) {
  const nav = useNavigate();
  const { dark, toggle } = useTheme();
  return (
    <nav>
      <button className="mark" type="button" aria-label="Drape home" onClick={() => nav("/")}>
        <b>Drape</b>
        <span className="seal">Beta</span>
      </button>
      <div className="navr">
        {me && (
          <>
            <Link className="eyebrow navlink" to="/history">
              History
            </Link>
            <Link className="eyebrow navlink" to="/profile">
              Profile
            </Link>
            {me.user.is_guest && (
              <Link className="eyebrow navlink" to="/signin">
                Sign in
              </Link>
            )}
          </>
        )}
        <button type="button" className="eyebrow" aria-label={dark ? "Switch to day theme" : "Switch to night theme"} onClick={toggle}>
          {dark ? "Day" : "Night"}
        </button>
      </div>
    </nav>
  );
}

function BackBar() {
  const loc = useLocation();
  const nav = useNavigate();
  const show = loc.pathname !== "/" && loc.pathname !== "/welcome" && loc.pathname !== "/onboarding" && loc.pathname !== "/favorite";
  if (!show) return null;
  return (
    <div className="backbar">
      <button className="backbtn" type="button" onClick={() => (loc.key !== "default" ? nav(-1) : nav("/"))}>
        <BackChevron />
        Back
      </button>
    </div>
  );
}

function Home() {
  const { me } = useApp();
  // A first visit says hello once; the board then starts new shoppers on their favorite piece.
  if (needsOnboarding(me) && me.user.is_guest && !welcomed()) return <Navigate to="/welcome" replace />;
  return <Board />;
}

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraftState] = useState<BoardDraft>(emptyDraft);
  const [fav, setFavState] = useState<FavDraft>(emptyFavDraft);
  const nav = useNavigate();
  const loc = useLocation();

  const boot = useCallback(() => {
    setErr(null);
    // GET /api/me; the client turns a 401 not_signed_in into POST /api/auth/guest + retry.
    Promise.all([api.me(), api.meta()])
      .then(([m, mt]) => {
        setMe(m);
        setMeta(mt);
      })
      .catch((e: unknown) => setErr(errorMessage(e)));
  }, []);
  useEffect(boot, [boot]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc.pathname]);

  const setDraft = useCallback((f: (d: BoardDraft) => BoardDraft) => setDraftState(f), []);
  const setFav = useCallback((f: (d: FavDraft) => FavDraft) => setFavState(f), []);
  const newBoard = useCallback(() => {
    setDraftState(emptyDraft());
    nav("/");
    window.scrollTo(0, 0);
  }, [nav]);

  const ctx = useMemo<AppCtx | null>(
    () => (me && meta ? { me, meta, setMe, draft, setDraft, newBoard, fav, setFav } : null),
    [me, meta, draft, setDraft, newBoard, fav, setFav],
  );

  return (
    <div className="wrap">
      <Nav me={me} />
      <BackBar />
      <main id="app" aria-live="polite" aria-busy={!ctx && !err}>
        {err && (
          <section className="view enter">
            <div className="banner bad">
              <b>We couldn't load Drape.</b>
              <p className="small">{err}</p>
            </div>
            <button className="btn primary" type="button" onClick={boot}>
              Try again
            </button>
          </section>
        )}
        {!err && !ctx && (
          <section className="view">
            <p className="eyebrow">Loading…</p>
          </section>
        )}
        {ctx && (
          <Ctx.Provider value={ctx}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/welcome" element={<SignIn welcome />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/favorite" element={<FavoriteStep />} />
              <Route path="/check/:id" element={<Board />} />
              <Route path="/check/:id/report" element={<Report />} />
              <Route path="/history" element={<History />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Ctx.Provider>
        )}
      </main>
      <ToastHost />
    </div>
  );
}
